import {
  Button,
  Checkbox,
  Fade,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import { Formik } from "formik";
import { isEmpty } from "lodash-es";
import { useState } from "react";
import * as Yup from "yup";

import FormContentBlock from "@/App/Layouts/FormContentBlock";
import { useBreadcrumbReplacements } from "@/common/contexts/breadcrumbs";
import { FileItem } from "@/common/fileItem";
import { FormikHelper } from "@/common/helpers/formik";
import { useApiRequest } from "@/common/hooks/api/useApiRequest";
import useMounted from "@/common/hooks/mount/useMounted";
import useAppSnackbar from "@/common/hooks/useAppSnackbar";
import { useUserAffiliation } from "@/common/hooks/user/useUserAffiliation";
import { BaseEntityDefaultValues } from "@/common/ts/entity";
import { BaseFormikValues } from "@/common/ts/error";
import { ValidationHelper } from "@/common/validation";
import { apiClient } from "@/core/api/ApiClient";
import {
  AssetCreateDto,
  AssetDto,
  AssetEntityType,
  AssetSpecDto,
  AssetSpecInputDto,
  AssetUpdateDto,
  EntityType,
} from "@/core/api/generated/v1";

import FoldableBlock from "../../Display/FoldableBlock";
import GeneralValidationError from "../../Error/GeneralValidationError";
import FileUploader from "../../Files/FileUploader";
import FormActions from "../../Form/FormActions";
import AppTextArea from "../../Form/Input/AppTextArea";
import AccessoriesSelectOrCreate from "../Accessory/AccessoriesSelectOrCreate";
import EntityAffiliationInputV2 from "../EntityAffiliation/EntityAffiliationInputV2";
import GeneralAttachedTagsInput from "../General/GeneralTag/GeneralAttachedTagsInput";
import GeneralStrictEntityRelationInput from "../General/Input/GeneralStrictEntityRelationInput";
import BaseEntityCreateUpdate, {
  BaseEntityCreateUpdateInheritableProps,
} from "../components/BaseEntityCreateUpdate";
import AssetSpecInput, {
  assetSpecToInputDto,
  assetSpecToInputDtoFormatBeforePost,
} from "./AssetSpecInput";

type DefaultValues = BaseEntityDefaultValues & {
  entity?: AssetCreateDto["entity"];
  name?: AssetCreateDto["name"];
  departmentIds?: AssetCreateDto["departmentIds"];
  locationIds?: AssetCreateDto["locationIds"];
  spec?: AssetSpecDto | AssetSpecInputDto | null;
};

export interface AssetCreateUpdateOwnProps extends BaseEntityCreateUpdateInheritableProps<
  AssetDto,
  DefaultValues
> {}

export type AssetCreateUpdateProps = AssetCreateUpdateOwnProps;

export default function AssetCreateUpdate({
  entityId,
  defaultValues,
  onCreate,
  onUpdate,
  onSave,
}: AssetCreateUpdateProps) {
  const mounted = useMounted();
  const { enqueueSnackbar } = useAppSnackbar();
  const userAffiliation = useUserAffiliation();
  const isCreate = !entityId;
  const isEdit = !!entityId;

  const [isUploadingFiles, setIsUploadingFiles] = useState(false);

  const assetRequest = useApiRequest(
    apiClient.assetsApi.apiV1AssetsAssetIdGet,
    {
      nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
      assetId: entityId!,
    },
    {
      skip: !entityId,
    },
  );
  const asset = assetRequest?.data;

  useBreadcrumbReplacements({
    waitTimeout: 10_000,
    idBreadcrumb: asset && {
      idValue: asset.id!,
      newTitle: asset.localNumber || "",
    },
  });

  return (
    <BaseEntityCreateUpdate
      entityType={EntityType.Asset}
      entityId={entityId}
      entity={asset}
      entityRequest={assetRequest}
      isIniting={userAffiliation.isIniting}
    >
      <Formik<
        BaseFormikValues &
          AssetCreateDto &
          AssetUpdateDto & {
            isAttachmentFilesHaveErrors: boolean;
            initialAttachments: AssetCreateDto["attachments"];
            uploadedAttachments?: FileItem[];
          }
      >
        enableReinitialize={true}
        initialValues={{
          isAttachmentFilesHaveErrors: false,
          entity:
            defaultValues?.entity || asset?.entity
              ? {
                  entityType:
                    defaultValues?.entity?.entityType ||
                    (asset?.entity?.entityType as AssetEntityType) ||
                    undefined,
                  entityId: defaultValues?.entity?.entityId || asset?.entity?.entityId || undefined,
                }
              : undefined,
          name: defaultValues?.name || asset?.name || undefined,
          description: asset?.description || undefined,
          departmentIds:
            (!isEmpty(defaultValues?.departmentIds) ? defaultValues!.departmentIds : undefined) ||
            (!isEmpty(asset?.departmentIds) ? asset!.departmentIds : undefined) ||
            (!isEmpty(userAffiliation.departments)
              ? userAffiliation.departments!.map((d) => d.id!)
              : undefined),
          locationIds:
            (!isEmpty(defaultValues?.locationIds) ? defaultValues!.locationIds : undefined) ||
            (!isEmpty(asset?.locationIds) ? asset!.locationIds : undefined) ||
            (!isEmpty(userAffiliation.locations)
              ? userAffiliation.locations!.map((l) => l.id!)
              : undefined),
          spec:
            (asset?.spec && assetSpecToInputDto(asset?.spec)) ||
            (defaultValues?.spec && assetSpecToInputDto(defaultValues?.spec)) ||
            (defaultValues?.entity?.entityType && {
              entityType: defaultValues.entity.entityType,
            }) ||
            undefined,
          accessoryIds: asset?.accessories?.map((x) => x.id!) || undefined,
          attachments: asset?.attachments || undefined,
          settings: asset?.settings || undefined,
          tags: asset?.tags || undefined,

          initialAttachments: asset?.attachments || undefined,

          submit: "",
        }}
        validationSchema={Yup.object().shape({
          // vehicleId: Yup.string().required("Vehicle is required"),
        })}
        onSubmit={async (values, { setFieldError, setStatus, setSubmitting }) => {
          const values2: typeof values = {
            ...values,
            spec: assetSpecToInputDtoFormatBeforePost(values.spec),
          };

          try {
            if (isCreate) {
              const response = await apiClient.assetsApi.apiV1AssetsPost({
                nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
                assetCreateDto: {
                  ...values2,
                },
              });
              enqueueSnackbar("Asset created.", { variant: "success" });
              onCreate && onCreate(response.data);
              onSave && onSave(response.data);
            } else {
              const response = await apiClient.assetsApi.apiV1AssetsAssetIdPut({
                nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
                assetId: entityId,
                assetUpdateDto: {
                  ...values2,
                },
              });
              enqueueSnackbar("Asset updated.", { variant: "success" });
              onUpdate && onUpdate(response.data);
              onSave && onSave(response.data);
            }

            if (mounted.current) {
              setStatus({ success: true });
              setSubmitting(false);
            }
          } catch (err: any) {
            if (mounted.current) {
              ValidationHelper.handleApiErrorResponseFormik(err, setFieldError);
              setStatus({ success: false });
              setSubmitting(false);
            }
          }
        }}
      >
        {(formikProps) => {
          const {
            errors,
            handleBlur,
            handleChange,
            handleSubmit,
            isSubmitting,
            touched,
            values,
            setFieldValue,
          } = formikProps;

          const specFormikProps = FormikHelper.getSubProps(formikProps, "spec", (v) => v.spec);

          return (
            <form noValidate onSubmit={handleSubmit}>
              <FormContentBlock>
                {/* Tags */}
                <FormControl margin='dense' fullWidth>
                  <GeneralAttachedTagsInput
                    value={values.tags}
                    onChange={(newValue) => {
                      setFieldValue("tags", newValue);
                    }}
                  />
                  <FormHelperText error>
                    {ValidationHelper.getFormikErrorsAsString(errors.tags, {
                      isIncludeNested: false,
                    })}
                  </FormHelperText>
                </FormControl>

                {/* Entity */}
                <FormControl margin='dense' fullWidth required>
                  <GeneralStrictEntityRelationInput
                    onlyEntityTypes={Object.values(AssetEntityType)}
                    value={values.entity}
                    disabled={isEdit}
                    entityTypeAutocompleteProps={{
                      required: true,
                      textFieldProps: {
                        error: Boolean(errors?.entity),
                        helperText: ValidationHelper.getFormikErrorsAsString(errors?.entity),
                      },
                    }}
                    entityAutocompleteProps={{
                      label: "Entity",
                      required: true,
                      textFieldProps: {
                        error: Boolean(errors?.entity),
                        helperText: ValidationHelper.getFormikErrorsAsString(errors?.entity),
                      },
                      searchFilters: {
                        assetMeta: {
                          hasAsset: false,
                        },
                      },
                    }}
                    onChange={async (newValue) => {
                      setFieldValue(`entity`, newValue);
                      setFieldValue(`spec`, undefined);
                      setFieldValue(`spec.entityType`, newValue?.entityType as AssetEntityType);

                      // infer spec
                      if (newValue && newValue.entityId) {
                        const inferredSpec = (
                          await apiClient.assetsApi.apiV1AssetsSpecInferFromEntityGet({
                            nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
                            entityType: newValue.entityType as AssetEntityType,
                            entityId: newValue.entityId,
                          })
                        ).data;
                        setFieldValue(`spec`, assetSpecToInputDto(inferredSpec));
                      }
                    }}
                  />
                </FormControl>

                <TextField
                  error={Boolean(touched.name && errors.name)}
                  fullWidth
                  helperText={touched.name && errors.name}
                  label='Name'
                  margin='dense'
                  name='name'
                  onBlur={handleBlur}
                  onChange={handleChange}
                  type='text'
                  value={values.name || ""}
                  variant='outlined'
                />

                <AppTextArea
                  error={Boolean(touched.description && errors.description)}
                  fullWidth
                  helperText={touched.description && errors.description}
                  mode='description'
                  margin='dense'
                  name='description'
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.description || ""}
                  variant='outlined'
                />

                {/* Entity affiliation */}
                <EntityAffiliationInputV2
                  departments={{
                    departmentIds: values.departmentIds,
                    onChange: (departmentIds) => {
                      setFieldValue("departmentIds", departmentIds);
                    },
                    error: errors.departmentIds,
                  }}
                  locations={{
                    locationIds: values.locationIds,
                    onChange: (locationIds) => {
                      setFieldValue("locationIds", locationIds);
                    },
                    error: errors.locationIds,
                  }}
                />

                <Fade
                  in={!!values.entity && !!values.entity.entityType && !!values.entity.entityId}
                  unmountOnExit
                >
                  <Stack spacing={2} sx={{ mt: 2 }}>
                    {/* AssetSpec */}
                    <FoldableBlock
                      defaultIsFolded
                      trigger={{
                        label: <Typography variant='subtitle1'>Asset spec *</Typography>,
                      }}
                    >
                      <AssetSpecInput
                        formikProps={specFormikProps}
                        displayProps={{
                          editEntityType: false,
                          enableEditEntityType: false,
                        }}
                      />
                    </FoldableBlock>

                    {/* Accessories */}
                    <FoldableBlock
                      defaultIsFolded
                      trigger={{
                        label: <Typography variant='subtitle1'>Accessories</Typography>,
                      }}
                    >
                      <FormControl margin='dense' fullWidth>
                        <AccessoriesSelectOrCreate
                          autocompleteProps={{
                            entityIds: values.accessoryIds,
                            entities: undefined,
                            isPreload: isCreate,
                            textFieldProps: {
                              error: Boolean(errors.accessoryIds),
                              helperText: ValidationHelper.getFormikErrorsAsString(
                                errors.accessoryIds,
                              ),
                            },
                            onChange: (newValues) => {
                              setFieldValue(
                                "accessoryIds",
                                newValues?.map((x) => x.id!),
                              );
                            },
                          }}
                          createFormPlacement='modal'
                          onCreate={(newValue) => {
                            setFieldValue("accessoryIds", [
                              ...(values.accessoryIds || []),
                              newValue.id!,
                            ]);
                          }}
                        />
                      </FormControl>
                    </FoldableBlock>

                    {/* Attachments */}
                    <FoldableBlock
                      defaultIsFolded
                      trigger={{
                        label: <Typography variant='subtitle1'>Attachments</Typography>,
                      }}
                    >
                      <FormControl fullWidth margin='none'>
                        <FileUploader
                          multiple
                          maxFiles={100}
                          defaultFiles={FileItem.createManyFrom(
                            values.uploadedAttachments ||
                              values.attachments ||
                              values.initialAttachments,
                          )}
                          onChange={(newFiles) => {
                            setFieldValue(
                              "attachments",
                              FileItem.toManyGeneralAttachmentInputDto(newFiles),
                            );
                            setFieldValue("uploadedAttachments", newFiles);
                          }}
                          onUploadStarted={() => {
                            setIsUploadingFiles(true);
                          }}
                          onUploadFinished={() => {
                            setIsUploadingFiles(false);
                          }}
                          onValidationStatusChange={(filesValidationStatus) => {
                            if (filesValidationStatus)
                              setFieldValue(
                                "isAttachmentFilesHaveErrors",
                                Object.values(filesValidationStatus).some((x) => x === false),
                              );
                          }}
                        />

                        {errors.attachments && (
                          <FormHelperText error>
                            {ValidationHelper.getFormikErrorsAsString(errors.attachments)}
                          </FormHelperText>
                        )}
                      </FormControl>
                    </FoldableBlock>

                    {/* Settings */}
                    <FoldableBlock
                      defaultIsFolded
                      trigger={{
                        label: <Typography variant='subtitle1'>Settings</Typography>,
                      }}
                    >
                      <Box>
                        <FormControl margin='dense'>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={values.settings?.isSyncSpec ?? false}
                                onChange={(e) =>
                                  setFieldValue("settings.isSyncSpec", e.target.checked)
                                }
                              />
                            }
                            label='Sync spec'
                          />
                          <FormHelperText>
                            If checked, asset spec will be automatically synced with the source
                            entity spec. For instance, changes to vehicle spec will be synced to its
                            asset.
                          </FormHelperText>
                        </FormControl>
                      </Box>
                    </FoldableBlock>
                  </Stack>
                </Fade>
              </FormContentBlock>

              <GeneralValidationError sx={{ my: 1 }} errors={errors} />

              <FormActions>
                <Button
                  color='primary'
                  loading={isSubmitting}
                  disabled={isUploadingFiles || values.isAttachmentFilesHaveErrors}
                  fullWidth
                  type='submit'
                  variant='contained'
                >
                  Save
                </Button>
              </FormActions>
            </form>
          );
        }}
      </Formik>
    </BaseEntityCreateUpdate>
  );
}
